/**
 * One address per screen: `/` is the landing (the public root is the
 * PLG entry), `/onboard` the five-question setup, `/app/*` the six-step wizard, `/home` the
 * workspace a buyer lands in after paying, `/console` the operator prototype. The wizard's slugs are the same strings the old page used in its hash, so a
 * link a client already has keeps meaning the same step.
 */
export const Routes = {
  landing: '/',
  onboard: '/onboard',
  /** Регистрация почтой — вход воронки 4 (SaaS): продукт настраивается уже внутри кабинета. */
  signup: '/signup',
  app: '/app',
  appStep: '/app/:slug',
  home: '/home',
  /** Куда Stripe возвращает после внешней оплаты. Имя закреплено на стороне платежей — менять только вместе с ними. */
  thankYou: '/thank-you',
  console: '/console',
  /** Вход кодом из письма для вернувшегося; закрытые экраны шлют сюда с `?next=`. */
  login: '/login',
  /** Where an email unsubscribe link lands after the server flips the flag. Public, no auth. */
  unsubscribed: '/unsubscribed',
  /**
   * Поиск, которым поделились ссылкой (containers/Share). Публичный: право смотреть даёт подпись в
   * адресе (server/src/http/shareToken.ts), а не вход и не cookie.
   */
  share: '/share/:token',
} as const

/**
 * Адрес ссылки «поделиться». `?v=s` — та же метка, что у ручной проверки воронки: открывший ссылку
 * получает воронку 4 (продукт без онбординга) и НЕ попадает в сплит-тест воронок. Без метки новый
 * посетитель ссылки лёг бы в пул теста, и покупки по ссылкам, которые продаёт человек, а не реклама,
 * считались бы конверсией воронки (server/src/ab/funnel.ts funnelFor).
 */
export const sharePath = (token: string) => `/share/${encodeURIComponent(token)}?v=s`

export const APP_STEP_SLUGS = [
  'product',
  'roles',
  'industries',
  'locations',
  'company',
  'launch',
] as const

export type AppStepSlug = (typeof APP_STEP_SLUGS)[number]

export const isAppStepSlug = (value: string | undefined): value is AppStepSlug =>
  Boolean(value) && (APP_STEP_SLUGS as readonly string[]).includes(value as string)

export const appStepPath = (slug: AppStepSlug) => `${Routes.app}/${slug}`

/** 1-based, because every label the surfaces print is 1-based ("step 3 of 6"). */
export const stepNumber = (slug: AppStepSlug) => APP_STEP_SLUGS.indexOf(slug) + 1

export const slugByStep = (step: number): AppStepSlug =>
  APP_STEP_SLUGS[Math.max(0, Math.min(APP_STEP_SLUGS.length - 1, step - 1))] as AppStepSlug
